import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { X, Languages } from "lucide-react";
import { SUPPORTED } from "@/i18n";

/**
 * <LanguagePrompt /> — one-time bottom bar offering visitors from
 * Arabic / French / Hindi-speaking regions a nudge to switch the UI
 * to their native language. Detects intent from `navigator.language`
 * (browser locale) and falls back to `navigator.languages` for
 * multi-preference matches.
 *
 * Behaviour rules (child-safe UX):
 *   • Never shown if the user is already in a matching language.
 *   • Never shown a second time after being dismissed (persisted in
 *     `we_lang_prompt_dismissed_v1`).
 *   • Never shown a second time after being accepted (the switch
 *     already happens).
 *   • Only shown after a 900ms delay so it doesn't fight with page
 *     hydration or the AgeSelector modal on first paint.
 *   • Sits at bottom-center, respects safe-area-inset for iOS PWA.
 */
const PROMPT_LANGS = [
  { code: "ar", label: "العربية",  cta: "التبديل إلى العربية",  match: ["ar"] },
  { code: "fr", label: "Français", cta: "Passer en Français",   match: ["fr"] },
  { code: "hi", label: "हिन्दी",   cta: "हिन्दी में बदलें",       match: ["hi"] },
];

const DISMISS_KEY = "we_lang_prompt_dismissed_v1";

function detectPromptLang(currentUiLang) {
  const cands = [];
  if (typeof navigator !== "undefined") {
    if (navigator.language) cands.push(navigator.language);
    if (Array.isArray(navigator.languages)) cands.push(...navigator.languages);
  }
  const lowered = cands.map((c) => (c || "").toLowerCase());
  for (const p of PROMPT_LANGS) {
    if (currentUiLang === p.code) continue;
    if (lowered.some((L) => p.match.some((m) => L === m || L.startsWith(m + "-")))) {
      return p;
    }
  }
  return null;
}

export default function LanguagePrompt() {
  const { i18n } = useTranslation();
  const [target, setTarget] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let dismissed = false;
    try { dismissed = localStorage.getItem(DISMISS_KEY) === "1"; } catch { /* ignore */ }
    if (dismissed) return;
    // Also skip when the child hasn't picked an age yet — the
    // FirstVisitCTA owns the first landing moment, and this prompt
    // would compete for the same real-estate. Once age is picked
    // (child returns to a subsequent session), THEN we can nudge them
    // toward their native language if navigator suggests one.
    let ageSeen = false;
    try { ageSeen = localStorage.getItem("we_age_seen") === "1"; } catch { /* ignore */ }
    if (!ageSeen) return;
    const t = detectPromptLang(i18n.language);
    if (!t) return;
    if (!SUPPORTED.some((s) => s.code === t.code)) return;
    setTarget(t);
    const timer = setTimeout(() => setVisible(true), 900);
    return () => clearTimeout(timer);
  }, [i18n.language]);

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, "1"); } catch { /* ignore */ }
    setVisible(false);
  };

  const accept = () => {
    if (target?.code) i18n.changeLanguage(target.code);
    try { localStorage.setItem(DISMISS_KEY, "1"); } catch { /* ignore */ }
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && target && (
        <motion.div
          key="lang-prompt"
          initial={{ y: 60, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 60, opacity: 0 }}
          transition={{ type: "spring", stiffness: 260, damping: 22 }}
          data-testid="lang-prompt"
          role="dialog"
          aria-live="polite"
          className="fixed inset-x-0 bottom-3 md:bottom-4 z-[9999] flex justify-center px-3 pointer-events-none"
          style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        >
          <div className="pointer-events-auto max-w-md w-full rounded-2xl border-[3px] border-slate-900 bg-yellow-100 shadow-[0_6px_0_0_rgba(15,23,42,1)] p-3 flex items-center gap-3">
            <span className="inline-flex items-center justify-center h-11 w-11 rounded-full border-[3px] border-slate-900 bg-white shrink-0">
              <Languages size={20} strokeWidth={3} />
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                {target.label}
              </p>
              <p className="text-sm font-bold text-slate-900 leading-tight truncate" dir={target.code === "ar" ? "rtl" : "ltr"}>
                {target.cta}
              </p>
            </div>
            <button
              type="button"
              onClick={accept}
              data-testid="lang-prompt-accept"
              className="shrink-0 inline-flex items-center gap-1 h-9 px-3 rounded-full border-[3px] border-slate-900 bg-emerald-400 hover:bg-emerald-500 font-black text-xs shadow-[0_3px_0_0_rgba(15,23,42,1)]"
            >
              {target.code === "ar" ? "نعم" : target.code === "hi" ? "हाँ" : "Oui"}
            </button>
            <button
              type="button"
              onClick={dismiss}
              data-testid="lang-prompt-dismiss"
              aria-label="Dismiss"
              className="shrink-0 inline-flex items-center justify-center h-9 w-9 rounded-full border-[3px] border-slate-900 bg-white font-bold text-xs"
            >
              <X size={16} strokeWidth={3} />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
